import React, { useEffect, useState } from "react";
import "../../universal.css";
import { orderedAirports } from "../../data/config";
import { URL_START } from "../../data/config";

const NM_TO_KM = 1.852; // Nautical miles to kilometers conversion

const TFC = () => {
  const [trafficData, setTrafficData] = useState([]);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  
  // Fetch traffic data function
  const fetchTraffic = async () => {
    try {
      const response = await fetch(`${URL_START}/api/traffic`);
      const data = await response.json();
      setTrafficData(data);
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error("Error fetching Traffic data:", error);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTraffic();
      }
    };
  
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Initial fetch
    fetchTraffic();
    
    // Update strictly every 60 seconds when tab is visible
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchTraffic();
      }
    }, 60000);
  
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  // Function to calculate distance between two lat/lon points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Radius of Earth in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // Process data into airport-wise counts
  const airportTraffic = {};
  orderedAirports.forEach((airport) => {
    airportTraffic[airport.icao] = { 
      depGround: 0, 
      depAirborne: 0, 
      arr: 0, 
      arr200NM: 0, 
      vfr: 0 
    };
  });

  trafficData.forEach((flight) => {
    const depIcao = flight.flight_plan?.departure;
    const arrIcao = flight.flight_plan?.arrival;
    const isVFR = flight.flight_plan?.flight_rules === "V";

    // Handle departures
    if (depIcao && airportTraffic[depIcao]) {
      if (isVFR) {
        airportTraffic[depIcao].vfr++;
      } else {
        const airport = orderedAirports.find(a => a.icao === depIcao);
        if (airport) {
          const distance = calculateDistance(
            airport.latitude, 
            airport.longitude, 
            flight.latitude, 
            flight.longitude
          );
          if (distance <= 5 * NM_TO_KM) {
            airportTraffic[depIcao].depGround++;
          } else {
            airportTraffic[depIcao].depAirborne++;
          }
        }
      }
    }
    
    // Handle arrivals
    if (arrIcao && airportTraffic[arrIcao]) {
      if (isVFR) {
        airportTraffic[arrIcao].vfr++;
      } else {
        const airport = orderedAirports.find(a => a.icao === arrIcao);
        if (airport) {
          const distance = calculateDistance(
            airport.latitude, 
            airport.longitude, 
            flight.latitude, 
            flight.longitude
          );
          if (distance > 200 * NM_TO_KM) {
            airportTraffic[arrIcao].arr200NM++;
          } else {
            airportTraffic[arrIcao].arr++;
          }
        }
      }
    }
  });

  return (
    <div className="text-black h-full p-0">
      <div className="overflow-y-auto text-black text-xs monospace-font pb-8 h-full">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-300 text-center">
              <th className="border p-1 text-left" style={{borderRight: "solid 1px gray"}}>AIRPORT</th>
              <th className="border p-1">DEP</th>
              <th className="border p-1">ARR</th>
              <th className="border p-1" style={{borderRight: "solid 1px gray"}}>VFR</th>
              <th className="border p-1">DEP Airborne</th>
              <th className="border p-1">ARR 200 NM +</th>
            </tr>
          </thead>
          <tbody>
            {orderedAirports.map((airport) => {
              const traffic = airportTraffic[airport.icao];
              const hasTraffic = traffic.depGround > 0 || traffic.depAirborne > 0 || traffic.arr > 0 || traffic.arr200NM > 0 || traffic.vfr > 0;
              
              if (hasTraffic) {
                return (
                  <tr key={airport.icao} className="bg-gray-100 border-b text-center">
                    <td className="border p-1 text-left" style={{borderRight: "solid 1px gray"}}>{airport.icao} - {airport.name}</td>
                    <td className={`border p-1 ${traffic.depGround > 0 ? 'bg-blue-200' : ''}`}>
                      {traffic.depGround || ""}
                    </td>
                    <td className={`border p-1 ${traffic.arr > 0 ? 'bg-blue-200' : ''}`}>
                      {traffic.arr || ""}
                    </td>
                    <td className={`border p-1 ${traffic.vfr > 0 ? 'bg-blue-200' : ''}`} style={{borderRight: "solid 1px gray"}}>
                      {traffic.vfr || ""}
                    </td>
                    <td className={`border p-1}`}>{traffic.depAirborne || ""}</td>
                    <td className={`border p-1}`}>{traffic.arr200NM || ""}</td>
                  </tr>
                );
              }
              return null;
            })}
          </tbody>
        </table>
        <div className="text-xs text-black m-2">
          {lastUpdateTime && `Updated from VATSIM ${lastUpdateTime.getUTCHours().toString().padStart(2, '0')}:${lastUpdateTime.getUTCMinutes().toString().padStart(2, '0')}z`}
        </div>
      </div>
    </div>
  );
};

export default TFC;